import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "./FpShell";
import { FpDownloadButtons } from "./FpDownloadButtons";
import { EmptyState } from "@/components/shared";
import { fpBase, inr, monthLabel, shortInr, useFpContext } from "@/lib/fp";

/** The board pack: the five statements a promoter reads each month, on one clean screen.
 *  Read-only by design — every figure is the approved engine output. Explorer, the Output pack
 *  and every other workspace tab stay exactly as they are. */

const money = (v) => (v === null || v === undefined ? "—" : inr(v));
const ratio = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)}×`);

const Section = ({ kicker, title, note, children, testid }) => (
  <section className="panel overflow-hidden" data-testid={testid}>
    <div className="border-b border-border px-6 py-4">
      <p className="label-caps">{kicker}</p>
      <h3 className="font-display text-lg text-navy">{title}</h3>
      {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    </div>
    {children}
  </section>
);

const Line = ({ label, value, strong, indent }) => (
  <div className={`flex items-center justify-between gap-4 px-6 py-2 ${strong ? "bg-muted/40" : ""}`}>
    <p className={`text-sm ${indent ? "pl-4" : ""} ${strong ? "font-semibold text-navy" : "text-foreground/85"}`}>
      {label}
    </p>
    <p className={`font-mono tabular-nums ${strong ? "text-sm font-semibold text-navy" : "text-sm text-navy"}`}>
      {money(value)}
    </p>
  </div>
);

/* ---------------------------------------------------------- 1 · executive summary */
const ExecSummary = ({ l1, fcf, testid }) => (
  <Section kicker="Report 1 · Level 1" title="Executive summary" testid={testid}
           note="The month's earnings chain: revenue down to profit after tax, every line tied to the approved classifications.">
    <div className="divide-y divide-border">
      <Line label="Revenue" value={l1.revenue} strong />
      <Line label="Less: production variable cost" value={l1.pvc} indent />
      <Line label="Less: distribution variable cost" value={l1.dvc} indent />
      {l1.net_fg_stock ? <Line label="Less: net FG stock movement" value={l1.net_fg_stock} indent /> : null}
      <Line label="Throughput / contribution" value={l1.throughput} strong />
      <Line label="Less: fixed cost before interest" value={l1.fixed_expenses} indent />
      <Line label="EBITDA · operating profit" value={l1.ebitda} strong />
      <Line label="Less: depreciation" value={l1.depreciation} indent />
      <Line label="Other / non-operating income" value={l1.other_income} indent />
      <Line label="Less: interest" value={fcf.interest} indent />
      <Line label="Profit before tax" value={fcf.pbt} indent />
      <Line label="Less: tax" value={fcf.tax} indent />
      <Line label="Profit after tax" value={fcf.pat} strong />
    </div>
  </Section>
);

/* ---------------------------------------------------------- 2 · budget vs actual */
const BudgetActual = ({ bva, testid }) => (
  <Section kicker="Report 2 · plan vs performance" title="Budget vs actual" testid={testid}
           note={`Source: ${bva.source}. Adverse variances read in red.`}>
    {!(bva.rows || []).length ? (
      <p className="px-6 py-5 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        No budget loaded yet — upload a budget statement under Statements, or set targets on the
        Budgets tab, and this report fills itself in.
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            <tr>
              <th className="px-6 py-2 text-left">Metric</th>
              <th className="px-4 py-2 text-right">Actual</th>
              <th className="px-4 py-2 text-right">% of sales</th>
              <th className="px-4 py-2 text-right">Budget</th>
              <th className="px-4 py-2 text-right">% of sales</th>
              <th className="px-4 py-2 text-right">Deviation</th>
              <th className="px-6 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {bva.rows.map((r) => (
              <tr key={r.metric} data-testid={`${testid}-${r.metric.replace(/\W+/g, "-")}`}>
                <td className="px-6 py-2 text-navy">{r.metric}</td>
                <td className="px-4 py-2 text-right font-mono tabular-nums">{money(r.actual)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-navy">
                  {r.actual_pct_of_sales != null ? `${r.actual_pct_of_sales}%` : "—"}
                </td>
                <td className="px-4 py-2 text-right font-mono tabular-nums text-muted-foreground">{money(r.budget)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-muted-foreground">
                  {r.budget_pct_of_sales != null ? `${r.budget_pct_of_sales}%` : "—"}
                </td>
                <td className={`px-4 py-2 text-right font-mono tabular-nums ${
                  r.status === "Adverse" ? "text-destructive" : "text-success"}`}>
                  {money(r.variance)}{r.variance_pct !== null ? ` (${r.variance_pct}${r.pct_point ? " pp" : "%"})` : ""}
                </td>
                <td className="px-6 py-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);

/* ------------------------------------------- 3 · throughput to fixed expense ratio */
const CoverageTable = ({ title, lines, goal, testid }) => (
  <div data-testid={testid}>
    <p className="border-t border-border bg-muted/30 px-6 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy">
      {title}
    </p>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[680px] text-xs">
        <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          <tr>
            <th className="px-6 py-2 text-left">Line</th>
            <th className="px-4 py-2 text-right">Revenue</th>
            <th className="px-4 py-2 text-right">Throughput</th>
            <th className="px-4 py-2 text-right">Fixed expense</th>
            <th className="px-4 py-2 text-right">Coverage</th>
            <th className="px-4 py-2 text-right">Goal</th>
            <th className="px-6 py-2 text-right">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {lines.map((l) => (
            <tr key={l.label} data-testid={`${testid}-row-${l.label.replace(/\W+/g, "-")}`}>
              <td className="px-6 py-2 text-navy">{l.label}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums">{money(l.revenue)}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums font-semibold">{money(l.throughput)}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums">{l.fixed ? money(l.fixed) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums font-semibold">
                {l.ratio === null || l.ratio === undefined ? "—" : `${l.ratio}×`}
              </td>
              <td className="px-4 py-2 text-right font-mono tabular-nums text-muted-foreground">
                {l.ratio === null || l.ratio === undefined ? "—" : `${goal}×`}
              </td>
              <td className="px-6 py-2 text-right">
                {l.status === "met" && (
                  <span className="rounded-full bg-success/20 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-success">On goal</span>)}
                {l.status === "below" && (
                  <span className="rounded-full bg-destructive/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-destructive">Below goal</span>)}
                {!l.status && <span className="text-muted-foreground">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Coverage = ({ performance, testid }) => {
  const cats = performance?.categories || [];
  const outs = performance?.outlets || [];
  return (
    <Section kicker="Report 3 · does each line pay its way" testid={testid}
             title="Throughput to fixed expense ratio"
             note={`Throughput ÷ fixed expense carried, per production category and per outlet. Coverage goal: ${performance?.goal ?? "—"}×.`}>
      {!cats.length && !outs.length ? (
        <p className="px-6 py-5 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
          Coverage lines appear once categories and outlets carry approved tags.
        </p>
      ) : (
        <>
          {cats.length > 0 && (
            <CoverageTable title="Production category wise" lines={cats}
                           goal={performance.goal} testid={`${testid}-categories`} />)}
          {outs.length > 0 && (
            <CoverageTable title="Sub-channel / outlet wise" lines={outs}
                           goal={performance.goal} testid={`${testid}-outlets`} />)}
        </>
      )}
    </Section>
  );
};

/* ------------------------------- 4 + 5 · cash flow statement and solvency, month by month */
const CF_ROWS = [
  ["EBITDA", "ebitda", true], ["Less: working capital increase", "increase_in_working_capital"],
  ["Operating cash flow", "operating_cash_flow", true], ["Less: maintenance capex", "maintenance_capex"],
  ["Free cash flow", "free_cash_flow", true], ["Less: growth capex", "growth_capex"],
  ["Less: principal repayment", "principal_repayment"], ["Less: target dividend", "target_dividend"],
  ["Less: target cash reserve", "target_cash_reserve"],
  ["Surplus / (deficit) for the owners", "surplus_deficit", true],
];

const CashFlowMoM = ({ tenantId, period, testid }) => {
  const { ctx } = useFpContext();
  const [cols, setCols] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!tenantId || !period) return;
      const y = parseInt(period.slice(0, 4), 10), mo = parseInt(period.slice(5, 7), 10);
      const fyStart = mo >= 4 ? y : y - 1;
      const months = (ctx?.periods || [])
        .filter((p) => {
          const py = parseInt(p.slice(0, 4), 10), pm = parseInt(p.slice(5, 7), 10);
          return (pm >= 4 ? py : py - 1) === fyStart && p <= period;
        })
        .sort();
      const res = await Promise.all([
        ...months.map((p) => api.get(`${fpBase(tenantId)}/outputs`, { params: { period: p, basis: "month" } })),
        api.get(`${fpBase(tenantId)}/outputs`, { params: { period, basis: "ytd" } }),
      ]);
      if (!alive) return;
      setCols([...months.map((p, i) => ({ key: p, label: monthLabel(p), data: res[i].data })),
               { key: "ytd", label: "Cumulative", data: res[months.length].data }]);
    };
    load().catch(() => alive && setCols([]));
    return () => { alive = false; };
  }, [tenantId, period, ctx?.periods]);

  if (!cols?.length) return null;
  const bs = (c) => c.data?.balance_sheet || {};
  const sc = (c) => c.data?.statements?.controls || {};
  const solvency = [
    ["Total debt", (c) => money(bs(c).debt_closing)],
    ["Equity / net worth", (c) => money(bs(c).equity)],
    ["Debt-equity ratio", (c) => ratio(bs(c).equity ? bs(c).debt_closing / bs(c).equity : null)],
    ["Current ratio", (c) => ratio(sc(c)["Current Liabilities"]
      ? sc(c)["Current Assets"] / sc(c)["Current Liabilities"] : null)],
    ["Net working capital", (c) => money(sc(c)["Current Liabilities"] !== undefined
      ? (sc(c)["Current Assets"] ?? 0) - sc(c)["Current Liabilities"] : null)],
  ];
  return (
    <Section kicker="Reports 4 + 5 · month on month" title="Cash flow statement & solvency"
             testid={testid}
             note="Each column is one month; the last column accumulates April to the selected month. Balance-sheet lines show the month's closing position.">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            <tr>
              <th className="px-6 py-2 text-left">Line</th>
              {cols.map((c) => (
                <th key={c.key} className={`px-4 py-2 text-right ${c.key === "ytd" ? "text-terracotta" : ""}`}
                    data-testid={`${testid}-col-${c.key}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {CF_ROWS.map(([label, key, strong]) => (
              <tr key={key} className={strong ? "bg-muted/40" : ""} data-testid={`${testid}-${key}`}>
                <td className={`px-6 py-2 ${strong ? "font-semibold text-navy" : "text-foreground/85"}`}>{label}</td>
                {cols.map((c) => (
                  <td key={c.key} className={`px-4 py-2 text-right font-mono tabular-nums ${
                    c.key === "ytd" ? "font-semibold text-navy" : "text-navy"}`}>
                    {money((c.data?.fcf || {})[key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td colSpan={cols.length + 1}
                  className="bg-navy/5 px-6 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy">
                Capital &amp; liquidity · closing position
              </td>
            </tr>
            {solvency.map(([label, fn]) => (
              <tr key={label} data-testid={`${testid}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <td className="px-6 py-2 text-foreground/85">{label}</td>
                {cols.map((c) => (
                  <td key={c.key} className="px-4 py-2 text-right font-mono tabular-nums text-navy">{fn(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

export default function FpExecutivePack() {
  const { tenantId, ctx, period, basis } = useFpContext();
  const [m, setM] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/outputs`, { params: { period, basis } });
      setM(data);
    } catch (e) { setM({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  return (
    <FpShell
      title="Executive pack"
      description="The five statements the leadership reads each month — executive summary, budget vs actual, throughput-to-fixed-expense coverage, the cash flow statement, and solvency ratios. Read-only: every figure is the approved engine output."
      actions={<FpDownloadButtons />}>
      {ctx?.error ? (
        <EmptyState testid="fp-exec-no-access" title="Not available" message={ctx.error} />
      ) : !period ? (
        <EmptyState testid="fp-exec-empty" title="No financial statements yet"
                    message="Upload a month's statements under Statements and the pack builds itself." />
      ) : m?.error ? (
        <EmptyState testid="fp-exec-error" title="Unavailable" message={m.error} />
      ) : !m ? (
        <p className="label-caps animate-pulse" data-testid="fp-exec-loading">Building the executive pack</p>
      ) : (
        <div className="space-y-6" data-testid="fp-executive-pack">
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4" data-testid="fp-exec-headline">
            {[["Revenue", m.levels.level1.revenue], ["Throughput", m.levels.level1.throughput],
              ["EBITDA", m.levels.level1.ebitda], ["Free cash flow", m.fcf.free_cash_flow]].map(([l, v]) => (
              <div key={l} className="panel p-5" data-testid={`fp-exec-hl-${l.toLowerCase().replace(/\W+/g, "-")}`}>
                <p className="label-caps">{l}</p>
                <p className="mt-1 font-display text-2xl text-navy">{shortInr(v)}</p>
                <p className="mt-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">{inr(v)}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ExecSummary l1={m.levels.level1} fcf={m.fcf} testid="fp-exec-summary" />
            <div className="space-y-6">
              <BudgetActual bva={m.budget_vs_actual || {}} testid="fp-exec-bva" />
            </div>
          </div>
          <Coverage performance={m.levels?.level2?.performance} testid="fp-exec-coverage" />
          <CashFlowMoM tenantId={tenantId} period={period} testid="fp-exec-cashflow" />
        </div>
      )}
    </FpShell>
  );
}
